import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IdleModalProps {
  transcriptTime: number | null;
}

// Timer constants in milliseconds
const SHOW_MODAL_TIME = 3 * 60 * 1000; // 3 minutes
const STOP_SESSION_TIME = 60 * 1000; // 1 minute after modal shows (total 4 minutes)
const COUNTDOWN_INITIAL_TIME = 60; // 60 seconds for countdown display

export const IdleModal: React.FC<IdleModalProps> = ({ transcriptTime }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(COUNTDOWN_INITIAL_TIME);

  const showModalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stopSessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handlePageReload = () => {
    window.location.replace(location.pathname);
  };

  const clearAllTimers = () => {
    if (showModalTimeoutRef.current) {
      clearTimeout(showModalTimeoutRef.current);
      showModalTimeoutRef.current = null;
    }
    if (stopSessionTimeoutRef.current) {
      clearTimeout(stopSessionTimeoutRef.current);
      stopSessionTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const startNewTimer = () => {
    clearAllTimers();
    setShowModal(false);
    setTimeLeft(COUNTDOWN_INITIAL_TIME);

    // Set timer to show modal after 3 minutes
    showModalTimeoutRef.current = setTimeout(() => {
      setShowModal(true);
      setTimeLeft(COUNTDOWN_INITIAL_TIME);

      // Start countdown timer
      countdownIntervalRef.current = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      // Set timer to stop session after 1 more minute
      stopSessionTimeoutRef.current = setTimeout(() => {
        handlePageReload();
      }, STOP_SESSION_TIME);
    }, SHOW_MODAL_TIME);
  };

  const handleKeepSession = () => {
    startNewTimer();
  };

  const handleClick = () => {
    handleKeepSession();
  };

  // Reset timer whenever transcriptTime changes
  useEffect(() => {
    if (transcriptTime !== null) {
      startNewTimer();
    }
  }, [transcriptTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  if (!showModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999]">
      <div className=" tc-mv-blur rounded-regular p-6 max-w-sm w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white text-xl font-semibold">
            {t('avatar.areYouStillThere')}
          </h2>
          <button
            onClick={handleClick}
            className="text-gray-500 hover:text-white">
            <i className="fa-solid fa-xmark text-xl text-white" />
          </button>
        </div>
        <p className="text-white mb-6 text-left">
          {t('avatar.sessionExpireWarning', { seconds: timeLeft })}
        </p>
        <div className="flex justify-end gap-4">
          <button
            onClick={handleClick}
            className="px-4 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-white/90 transition-colors font-[NapsterSans]">
            {t('avatar.imStillHere')}
          </button>
        </div>
      </div>
    </div>
  );
};
